import React from 'react'
import './AgentMonitoring.css'

const DEFAULT_HEARTBEAT_MS = 2 * 60 * 1000

function formatCapacity(value) {
  const bytes = Number(value)
  if (!Number.isFinite(bytes) || bytes <= 0) return '미보고'
  const gib = bytes / (1024 ** 3)
  return gib >= 1 ? `${Number(gib.toFixed(1))} GiB` : `${Math.round(bytes / (1024 ** 2))} MiB`
}

function heartbeat(agent, now, threshold) {
  const reportedAt = Date.parse(agent.last_seen || agent.lastSeen || '')
  if (!Number.isFinite(reportedAt)) return { fresh: false, label: '보고 없음' }
  const elapsed = now - reportedAt
  const fresh = elapsed >= -30_000 && elapsed <= threshold
  const age = elapsed < 60_000 ? '1분 이내' : `${Math.floor(elapsed / 60_000)}분 전`
  return { fresh, label: elapsed < -30_000 ? '시각 확인 필요' : age }
}

function cpuCores(agent) {
  const cores = Number(agent.cpu_cores ?? agent.cpuCores)
  return Number.isFinite(cores) && cores > 0 ? `${cores}코어` : '미보고'
}

function storageFresh(agent, now, threshold) {
  const reportedAt = Date.parse(agent.storage_seen_at || '')
  if (!Number.isFinite(reportedAt)) return false
  const elapsed = now - reportedAt
  return elapsed >= -30_000 && elapsed <= threshold
}

function storageMounts(agent) {
  if (!Array.isArray(agent.storage_mounts)) return []
  return agent.storage_mounts.filter((mount) => mount &&
    (mount.kind === 'block' || mount.kind === 'network') &&
    typeof mount.mount_point === 'string' && mount.mount_point.length > 0)
}

function StorageInventory({ agent, current }) {
  const mounts = storageMounts(agent)
  const hasReport = typeof agent.storage_seen_at === 'string' &&
    Number.isFinite(Date.parse(agent.storage_seen_at)) &&
    Array.isArray(agent.storage_mounts)

  return <div className="agent-monitoring__storage">
    <div className="agent-monitoring__storage-head">
      <h4>저장소 마운트</h4>
      <span className={`agent-monitoring__storage-state ${current && hasReport ? 'is-current' : 'is-stale'}`}>
        {current && hasReport ? '최근 보고' : hasReport ? '이전 보고 · 현재 상태 미확인' : '수집 시각 미보고 · 현재 상태 미확인'}
      </span>
    </div>
    {mounts.length === 0
      ? <p>{hasReport ? '보고된 마운트 없음' : '마운트 정보 없음'}</p>
      : <ul className="agent-monitoring__mounts">
        {mounts.map((mount, index) => <li key={`${mount.kind}:${mount.mount_point}:${index}`}>
          <span className="agent-monitoring__mount-kind">{mount.kind === 'block' ? '블록' : '네트워크'}</span>
          <span className="agent-monitoring__mount-path">{mount.mount_point}</span>
          <span className="agent-monitoring__mount-meta">
            {typeof mount.fs_type === 'string' && mount.fs_type ? `${mount.fs_type} · ` : ''}
            전체 {formatCapacity(mount.total_bytes)} · 여유 {formatCapacity(mount.available_bytes)}
          </span>
        </li>)}
      </ul>}
    <p className="agent-monitoring__storage-note">마운트 보고만 표시하며 저장소 연결·서비스 배포 성공은 확인하지 않습니다.</p>
  </div>
}

const ENGINE_LABEL = { podman: 'Podman', docker: 'Docker' }
const RUNTIME_LABEL = { ready: '엔진 응답', unavailable: '실행 불가', not_detected: '미설치' }

function RuntimeInventory({ agent, current, now, threshold }) {
  const report = agent.runtime_capability
  const runtimes = agent.runtime_capability_source === 'self-reported-unverified' &&
    Array.isArray(report?.runtimes) ? report.runtimes.filter(item =>
      item && ENGINE_LABEL[item.engine] && RUNTIME_LABEL[item.status]) : []
  const reportedAt = Date.parse(agent.runtime_capability_seen_at || '')
  const fresh = current && Number.isFinite(reportedAt) && now - reportedAt >= -30_000 && now - reportedAt <= threshold
  return <div className="agent-monitoring__storage">
    <div className="agent-monitoring__storage-head">
      <h4>컨테이너 엔진</h4>
      <span className={`agent-monitoring__storage-state ${fresh ? 'is-current' : 'is-stale'}`}>
        {runtimes.length ? fresh ? '최근 자가 보고 · 실행 미검증' : '이전 자가 보고 · 현재 상태 미확인' : '엔진 능력 미보고'}
      </span>
    </div>
    {runtimes.length > 0 && <ul className="agent-monitoring__mounts">{runtimes.map(item =>
      <li key={item.engine}>{ENGINE_LABEL[item.engine]} · {RUNTIME_LABEL[item.status]} (자가 보고)
        {typeof item.version === 'string' && /^[0-9][A-Za-z0-9.+_~-]{0,63}$/.test(item.version) ? ` · ${item.version}` : ''}</li>)}</ul>}
    <p className="agent-monitoring__storage-note">이 보고는 엔진 실행 권한·서비스 배포 성공을 증명하지 않습니다.</p>
  </div>
}

export default function AgentMonitoring({ agents = [], runtimeVerified = false,
  now = Date.now(), heartbeatThresholdMs = DEFAULT_HEARTBEAT_MS }) {
  const currentTime = now instanceof Date ? now.getTime() : Number(now)
  const safeNow = Number.isFinite(currentTime) ? currentTime : Date.now()
  const threshold = Number.isFinite(heartbeatThresholdMs) && heartbeatThresholdMs > 0
    ? Math.min(heartbeatThresholdMs, DEFAULT_HEARTBEAT_MS) : DEFAULT_HEARTBEAT_MS

  return <section className="agent-monitoring" aria-labelledby="agent-monitoring-title">
    <div className="agent-monitoring__heading">
      <h2 id="agent-monitoring-title">에이전트 모니터링</h2>
      <span className="agent-monitoring__count">등록 노드 {agents.length}대</span>
    </div>
    <p className="agent-monitoring__scope" role="status">
      {runtimeVerified
        ? '런타임 상태 확인됨. 노드 신호와 사양은 마지막 보고 기준입니다.'
        : '런타임 미검증: 컨테이너 실행·배포 성공·실제 사용률은 확인되지 않았습니다.'}
    </p>
    {agents.length === 0
      ? <p className="agent-monitoring__empty">조회된 에이전트가 없습니다.</p>
      : <div className="agent-monitoring__grid">
        {agents.map((agent, index) => {
          const signal = heartbeat(agent, safeNow, threshold)
          const online = agent.online === true && signal.fresh
          const name = agent.hostname || `에이전트 ${agent.peer_id ?? index + 1}`
          return <article className="agent-monitoring__card" key={agent.peer_id ?? name}>
            <div className="agent-monitoring__card-head">
              <h3>{name}</h3>
              <span className={`agent-monitoring__state ${online ? 'is-online' : 'is-offline'}`}>
                {online ? '온라인' : '오프라인'}
              </span>
            </div>
            <dl className="agent-monitoring__facts">
              <div><dt>마지막 신호</dt><dd>{signal.label}{!signal.fresh && signal.label !== '보고 없음' ? ' · 지연' : ''}</dd></div>
              <div><dt>운영체제</dt><dd>{agent.os_type || agent.osType || '미보고'}</dd></div>
              <div><dt>CPU 사양</dt><dd>{cpuCores(agent)}</dd></div>
              <div><dt>메모리 용량</dt><dd>{formatCapacity(agent.memory_bytes ?? agent.memoryBytes)}</dd></div>
              <div><dt>디스크 용량</dt><dd>{formatCapacity(agent.root_disk_bytes ?? agent.rootDiskBytes)}</dd></div>
            </dl>
            <StorageInventory agent={agent} current={online && storageFresh(agent, safeNow, threshold)} />
            <RuntimeInventory agent={agent} current={online} now={safeNow} threshold={threshold} />
          </article>
        })}
      </div>}
  </section>
}
